import { useState, useCallback } from "react";
import { api } from "../api";
import { logError } from "../utils/logger";

/**
 * Hook for managing comment like functionality
 *
 * @param {string} noteId - The ID of the note containing the comment
 * @param {string} commentId - The ID of the comment
 * @param {boolean} initialLiked - Initial liked state (from backend data)
 * @param {number} initialCount - Initial like count (from backend data)
 */
export function useCommentLike(
  noteId,
  commentId,
  initialLiked = false,
  initialCount = 0,
) {
  const [liked, setLiked] = useState(initialLiked);
  const [likeCount, setLikeCount] = useState(initialCount);
  const [loading, setLoading] = useState(false);

  const toggleLike = useCallback(async () => {
    if (loading || !noteId || !commentId) return;

    // Optimistic update
    const newLiked = !liked;
    const newCount = newLiked ? likeCount + 1 : Math.max(0, likeCount - 1);

    setLiked(newLiked);
    setLikeCount(newCount);
    setLoading(true);

    try {
      const response = await api.toggleCommentLike(noteId, commentId);

      // Update with backend response
      setLiked(response.data.liked);
      setLikeCount(response.data.count);
    } catch (err) {
      // Revert on error
      setLiked(!newLiked);
      setLikeCount(likeCount);
      logError("Failed to toggle comment like:", err);
    } finally {
      setLoading(false);
    }
  }, [noteId, commentId, liked, likeCount, loading]);

  return {
    liked,
    likeCount,
    toggleLike,
    loading,
  };
}
